import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import useAuth from "../../../hooks/useAuth";
import UseAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useSIP } from "../../../context/SIPContext";
import {
  COMMENT_URL,
  ADD_COMMENT_URL,
  FOLLOWUP_REPORT_URL,
  FOLLOWUP_BADGES_URL,
  REPORT_TAG_MAIN_URL,
  REPORT_TAG_CHILD_URL,
} from "../../../utils/ApiUrls";
import { buildCalendarDays } from "./utils";
import Cards from "./Cards";
import Calendar from "./Calendar";
import FollowupReport from "./FollowupReport";
import RecentCalls from "./RecentCalls";
import AnsweredPercentage from "./AnsweredPercentage";
import "./Dashboard2.css";

const Dashboard2 = () => {
  const { auth } = useAuth();
  const axiosPrivate = UseAxiosPrivate("dashboard2");
  const { makeCall } = useSIP();
  const isAdmin = auth?.roles?.includes("sys_admin");
  const hasWebRtc =
    (auth?.permissions?.data || []).some((p) => p.name === "WebRtc") ||
    (isAdmin && !!auth?.user_details?.sip_details);

  // ---- real dashboard data, from the same endpoints the existing Agent
  // dashboard (src/pages/Agent/Dashboard.js) already uses ----
  const [dashdata, setDashdata] = useState({
    total_calls: 0,
    answerd_calls: 0,
    call_duration: 0,
    answerd_percentage: [0, 0],
    followup_calls_count: 0,
  });
  const [recentCalls, setRecentCalls] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ---- per-call comment popup (frontend-only for now, no history yet) ----
  const [commentCallIndex, setCommentCallIndex] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const openCommentPopup = (index) => {
    setCommentDraft(recentCalls[index]?.comment || "");
    setCommentCallIndex(index);
  };
  const closeCommentPopup = () => {
    setCommentCallIndex(null);
    setCommentDraft("");
  };
  const [savingComment, setSavingComment] = useState(false);
  const saveComment = async () => {
    const call = recentCalls[commentCallIndex];
    const comment = commentDraft;

    if (call?.source === "virtual_sip" || call?.source === "contact_center") {
      if (!call.call_id) {
        Swal.fire({ icon: "error", title: "Missing call ID", text: "Can't save this comment." });
        return;
      }
      const [url, payload] =
        call.source === "virtual_sip"
          ? [COMMENT_URL, { comment, call_id: call.call_id }]
          : [ADD_COMMENT_URL, { comment, eventId: call.call_id }];

      setSavingComment(true);
      try {
        await axiosPrivate.post(url, payload);
      } catch (err) {
        console.log(err);
        setSavingComment(false);
        Swal.fire({ icon: "error", title: "Couldn't save comment", text: "Please try again." });
        return;
      }
      setSavingComment(false);
    }

    setRecentCalls((prev) =>
      prev.map((c, i) => (i === commentCallIndex ? { ...c, comment } : c))
    );
    closeCommentPopup();
    Swal.fire({ icon: "success", title: "Comment saved", timer: 1200, showConfirmButton: false });
  };
  const commentCall = commentCallIndex !== null ? recentCalls[commentCallIndex] : null;

  // ---- call tags (report_tag_main for the tag list, report_tag_child to
  // tag an individual call) ----
  const [reportTags, setReportTags] = useState([]);
  const [tagCallIndex, setTagCallIndex] = useState(null);
  const [savingTag, setSavingTag] = useState(false);

  const fetchReportTags = () => {
    const adminId = auth?.user_details?.parent;
    if (!adminId) return Promise.resolve();
    return axiosPrivate
      .get(`${REPORT_TAG_MAIN_URL}${adminId}/`)
      .then((res) => setReportTags(res.data?.report_tags || []))
      .catch((err) => console.log(err));
  };

  const openTagPopup = (index) => {
    setTagCallIndex(index);
  };
  const closeTagPopup = () => setTagCallIndex(null);
  const tagCall = tagCallIndex !== null ? recentCalls[tagCallIndex] : null;

  const parseCallTags = (tagStr) =>
    Array.from(
      new Set(
        (tagStr || "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
      )
    );

  // campaign (auto-dialed) calls are keyed by autocall_id against the
  // auto_call_record endpoint instead of call_record_id/call_record
  const getTagRecordRef = (call) =>
    call?.autocall_id
      ? { id: call.autocall_id, segment: "auto_call_record", key: "autocall_id" }
      : { id: call?.call_record_id, segment: "call_record", key: "call_record_id" };

  const fetchCallTags = ({ id, segment }) =>
    axiosPrivate
      .get(`${REPORT_TAG_CHILD_URL}${id}/${segment}/`)
      .then((res) => parseCallTags(res.data?.tag))
      .catch((err) => {
        console.log(err);
        return null;
      });

  const apiErrorMessage = (err, fallback) =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.tag?.[0] ||
    err?.response?.data?.detail ||
    fallback;

  const saveTag = async (tag) => {
    const call = recentCalls[tagCallIndex];
    const ref = getTagRecordRef(call);
    if (!ref.id) {
      Swal.fire({ icon: "error", title: "Missing call record ID", text: "Can't tag this call." });
      return;
    }
    if (call.report_tags?.includes(tag)) {
      Swal.fire({
        icon: "info",
        title: "Already tagged",
        text: `This call is already tagged as "${tag}".`,
      });
      return;
    }

    setSavingTag(true);
    try {
      await axiosPrivate.post(`${REPORT_TAG_CHILD_URL}${ref.id}/${ref.segment}/`, {
        [ref.key]: ref.id,
        tag,
      });
    } catch (err) {
      console.log(err);
      setSavingTag(false);
      Swal.fire({
        icon: "error",
        title: "Couldn't save tag",
        text: apiErrorMessage(err, "Please try again."),
      });
      return;
    }

    const updatedTags = await fetchCallTags(ref);
    setSavingTag(false);

    setRecentCalls((prev) =>
      prev.map((c, i) =>
        i === tagCallIndex ? { ...c, report_tags: updatedTags ?? [...(c.report_tags || []), tag] } : c
      )
    );
  };

  const removeTag = async (index, tag) => {
    const call = recentCalls[index];
    const ref = getTagRecordRef(call);
    if (!ref.id) {
      Swal.fire({ icon: "error", title: "Missing call record ID", text: "Can't remove this tag." });
      return;
    }

    setSavingTag(true);
    try {
      await axiosPrivate.delete(`${REPORT_TAG_CHILD_URL}${ref.id}/${ref.segment}/`, {
        data: { tag: tag.trim() },
      });
    } catch (err) {
      console.log(err);
      setSavingTag(false);
      Swal.fire({
        icon: "error",
        title: "Couldn't remove tag",
        text: apiErrorMessage(err, "Please try again."),
      });
      return;
    }

    const updatedTags = await fetchCallTags(ref);
    setSavingTag(false);

    setRecentCalls((prev) =>
      prev.map((c, i) =>
        i === index
          ? { ...c, report_tags: updatedTags ?? (c.report_tags || []).filter((t) => t !== tag) }
          : c
      )
    );
  };

  const fetchDashboardStats = () => {
    const userId = auth?.user_details?.user;
    if (!userId) return Promise.resolve();
    const BASE_URL = process.env.REACT_APP_API_URL;
    const config = { headers: { "Content-Type": "application/json" } };

    return axios
      .get(`${BASE_URL}/contactcentre/call_count/${userId}/agent/`, config)
      .then((res) => {
        const totalCalls = res.data.total_calls || 0;
        const answeredCalls = res.data.answerd_calls || 0;
        setDashdata((prev) => ({
          ...prev,
          total_calls: totalCalls,
          answerd_calls: answeredCalls,
          call_duration: res.data.call_duration,
          answerd_percentage:
            res.data.answerd_percentage || [answeredCalls, totalCalls - answeredCalls],
          followup_calls_count: res.data.followup_calls_count || 0,
        }));
      })
      .catch((err) => console.log(err));
  };

  const fetchRecentCalls = () => {
    const userId = auth?.user_details?.user;
    if (!userId) return Promise.resolve();
    const BASE_URL = process.env.REACT_APP_API_URL;
    const config = { headers: { "Content-Type": "application/json" } };

    return axios
      .get(`${BASE_URL}/contactcentre/agent_recent_calls/${userId}/`, config)
      .then((res) => {
        const calls = (res.data?.recent_calls || []).map((call) => ({
          ...call,
          report_tags: parseCallTags(call.tag),
        }));
        setRecentCalls(calls);
      })
      .catch((err) => console.log(err));
  };

  // ---- follow-ups (FollowUpReportContactcenter) ----
  const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const [followUps, setFollowUps] = useState([]);
  const [followUpsLoading, setFollowUpsLoading] = useState(false);
  const [followUpFromDate, setFollowUpFromDate] = useState(todayISO());
  const [followUpToDate, setFollowUpToDate] = useState(todayISO());

  const fetchFollowUps = () => {
    const userId = auth?.user_details?.user;
    if (!userId) return Promise.resolve();

    setFollowUpsLoading(true);
    return axiosPrivate
      .post(FOLLOWUP_REPORT_URL, {
        user_id: userId,
        from_date: followUpFromDate,
        to_date: followUpToDate,
        entry_cap: "yes",
        userType: "sys_user",
      })
      .then((res) => {
        setFollowUps(Array.isArray(res.data) ? res.data : res.data?.results || []);
      })
      .catch((err) => console.log(err))
      .finally(() => setFollowUpsLoading(false));
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchRecentCalls();
    fetchReportTags();
  }, [auth?.user_details?.user]);

  useEffect(() => {
    window.addEventListener("refresh-call-count", fetchDashboardStats);
    return () => window.removeEventListener("refresh-call-count", fetchDashboardStats);
  }, [auth?.user_details?.user]);

  useEffect(() => {
    fetchFollowUps();
  }, [auth?.user_details?.user, followUpFromDate, followUpToDate]);

  const handleRefreshDashboard = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchDashboardStats(),
        fetchRecentCalls(),
        fetchFollowUps(),
        fetchFollowUpBadges(),
        fetchReportTags(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const [answeredCount, notAnsweredCount] =
    dashdata.answerd_percentage?.length === 2 ? dashdata.answerd_percentage : [0, 0];
  const totalForPct = answeredCount + notAnsweredCount;
  const answeredPct = totalForPct ? Math.round((answeredCount / totalForPct) * 100) : 0;
  const CIRCUMFERENCE = 2 * Math.PI * 58;
  const dashOffset = CIRCUMFERENCE * (1 - answeredPct / 100);

  // ---- calendar (today highlighted; selecting a day filters My Follow-ups
  // to that date, clearing the selection goes back to the full list) ----
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const calendarDays = useMemo(() => buildCalendarDays(calMonth), [calMonth]);
  const [selectedDay, setSelectedDay] = useState(null);

  const goToMonth = (delta) => {
    setCalMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    setSelectedDay(null);
  };

  // ---- calendar day badges (follow-up counts per day, for the visible month) ----
  const [followUpBadges, setFollowUpBadges] = useState({});

  const fetchFollowUpBadges = () => {
    const userId = auth?.user_details?.user;
    if (!userId) return Promise.resolve();
    const year = calMonth.getFullYear();
    const month = calMonth.getMonth() + 1;

    return axiosPrivate
      .get(`${FOLLOWUP_BADGES_URL}${userId}/${year}/${month}/`)
      .then((res) => {
        const counts = {};
        (res.data || []).forEach((entry) => {
          const day = Number((entry.date || "").split("-")[0]);
          if (day) counts[day] = entry.followups || 0;
        });
        setFollowUpBadges(counts);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    fetchFollowUpBadges();
  }, [auth?.user_details?.user, calMonth]);

  useEffect(() => {
    if (selectedDay && selectedDay.startsWith("cur-")) {
      const day = Number(selectedDay.split("-")[1]);
      const iso = `${calMonth.getFullYear()}-${String(calMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      setFollowUpFromDate(iso);
      setFollowUpToDate(iso);
    } else {
      setFollowUpFromDate(todayISO());
      setFollowUpToDate(todayISO());
    }
  }, [selectedDay, calMonth]);

  const selectedDayLabel = useMemo(() => {
    if (!selectedDay || !selectedDay.startsWith("cur-")) return "";
    const day = Number(selectedDay.split("-")[1]);
    const d = new Date(calMonth.getFullYear(), calMonth.getMonth(), day);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }, [selectedDay, calMonth]);

  return (
    <div className="dash2">
      <main>
        <Cards dashdata={dashdata} />

        <div className="grid-2">
          <Calendar
            calMonth={calMonth}
            goToMonth={goToMonth}
            calendarDays={calendarDays}
            selectedDay={selectedDay}
            setSelectedDay={setSelectedDay}
            followUpBadges={followUpBadges}
          />

          <FollowupReport
            followUps={followUps}
            followUpsLoading={followUpsLoading}
            selectedDay={selectedDay}
            selectedDayLabel={selectedDayLabel}
            setSelectedDay={setSelectedDay}
            makeCall={makeCall}
            hasWebRtc={hasWebRtc}
          />
        </div>

        <div className="grid-2-b">
          <RecentCalls
            recentCalls={recentCalls}
            isRefreshing={isRefreshing}
            onRefresh={handleRefreshDashboard}
            openCommentPopup={openCommentPopup}
            commentCallIndex={commentCallIndex}
            commentCall={commentCall}
            commentDraft={commentDraft}
            setCommentDraft={setCommentDraft}
            closeCommentPopup={closeCommentPopup}
            saveComment={saveComment}
            savingComment={savingComment}
            reportTags={reportTags}
            tagCallIndex={tagCallIndex}
            tagCall={tagCall}
            openTagPopup={openTagPopup}
            closeTagPopup={closeTagPopup}
            saveTag={saveTag}
            removeTag={removeTag}
            savingTag={savingTag}
          />

          <AnsweredPercentage
            answeredCount={answeredCount}
            notAnsweredCount={notAnsweredCount}
            answeredPct={answeredPct}
            circumference={CIRCUMFERENCE}
            dashOffset={dashOffset}
          />
        </div>
      </main>
    </div>
  );
};

export default Dashboard2;
